/**
 * Lógica pura del repositorio de Redes Sociales: periodos, llaves de perfil, métricas que
 * aplican a cada red, foto de seguidores a una fecha y agregados del resumen. Sin React,
 * para poder probarla aparte.
 */

export const NETWORK_ORDER = ['Facebook', 'Instagram', 'LinkedIn', 'TikTok', 'YouTube', 'X'];

export const NETWORK_COLORS = {
    Facebook: '#1877F2',
    Instagram: '#D62976',
    LinkedIn: '#0A66C2',
    TikTok: '#111111',
    YouTube: '#FF0000',
    X: '#5f6368',
};

export const POST_METRICS = [
    'Impresiones', 'Alcance', 'Vistas', 'Vistas Orgánicas', 'Clics', 'Likes', 'Reacciones',
    'Comentarios', 'Compartidos', 'Guardados', 'Seguimientos', 'Actividad en Perfil',
    'Visitas al Perfil', 'Interacciones Plataforma', 'Interacciones',
    'Tiempo Total Visto (s)', 'Tiempo Promedio Visto (s)',
];

export const BLANK_LABEL = '(Sin dato)';

export const PERIOD_PRESETS = [
    { id: 'this_month', label: 'Este mes' },
    { id: 'last_month', label: 'Mes pasado' },
    { id: 'last_3', label: 'Últimos 3 meses' },
    { id: 'last_6', label: 'Últimos 6 meses' },
    { id: 'last_12', label: 'Últimos 12 meses' },
    { id: 'ytd', label: 'Este año' },
    { id: 'last_year', label: 'Año pasado' },
    { id: 'custom', label: 'Rango de meses' },
];

/** «2026-10» de una fecha local. */
export function monthKey(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/** Suma `delta` meses a «YYYY-MM». */
export function addMonths(month, delta) {
    const [y, m] = String(month).split('-').map(Number);
    return monthKey(new Date(y, m - 1 + delta, 1));
}

/** Meses entre `from` y `to` inclusive (máx. 120). */
export function monthsBetween(from, to) {
    const out = [];
    let cursor = from;
    while (cursor <= to && out.length < 120) {
        out.push(cursor);
        cursor = addMonths(cursor, 1);
    }
    return out;
}

/** Último día del mes «YYYY-MM» como «YYYY-MM-DD». */
export function monthEnd(month) {
    const [y, m] = month.split('-').map(Number);
    const last = new Date(y, m, 0).getDate();
    return `${month}-${String(last).padStart(2, '0')}`;
}

/**
 * Rango de meses de un preset. `custom` usa `from`/`to` tal cual.
 * @returns {{from: string, to: string}}
 */
export function resolvePeriod(preset, custom = {}, today = new Date()) {
    const current = monthKey(today);
    switch (preset) {
        case 'this_month': return { from: current, to: current };
        case 'last_month': { const prev = addMonths(current, -1); return { from: prev, to: prev }; }
        case 'last_3': return { from: addMonths(current, -2), to: current };
        case 'last_6': return { from: addMonths(current, -5), to: current };
        case 'last_12': return { from: addMonths(current, -11), to: current };
        case 'ytd': return { from: `${today.getFullYear()}-01`, to: current };
        case 'last_year': return { from: `${today.getFullYear() - 1}-01`, to: `${today.getFullYear() - 1}-12` };
        default: {
            const from = custom.from || current;
            const to = custom.to && custom.to >= from ? custom.to : from;
            return { from, to };
        }
    }
}

/** Periodo inmediato anterior de la misma duración. */
export function previousPeriod({ from, to }) {
    const length = monthsBetween(from, to).length;
    return { from: addMonths(from, -length), to: addMonths(from, -1) };
}

/** «oct 2026» o «ene – oct 2026». */
export function periodLabel({ from, to }) {
    const fmt = (month, withYear) => {
        const [y, m] = month.split('-').map(Number);
        const name = new Date(y, m - 1, 1).toLocaleDateString('es-MX', { month: 'short' }).replace('.', '');
        return withYear ? `${name} ${y}` : name;
    };
    if (from === to) return fmt(from, true);
    const sameYear = from.slice(0, 4) === to.slice(0, 4);
    return `${fmt(from, !sameYear)} – ${fmt(to, true)}`;
}

/** Llave única de un perfil: el mismo nombre existe en varias redes. */
export const profileKey = (row) => `${row.Red}|${row.Perfil}`;

export const isBlank = (value) => value == null || value === '' || value === BLANK_LABEL;

export const num = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

/** Filas cuyo perfil y red pasan el filtro (vacío = todos) y que no están omitidas. */
export function matchesSelection(row, { networks = [], profiles = [], excluded = [] }) {
    if (networks.length && !networks.includes(row.Red)) return false;
    const key = profileKey(row);
    if (profiles.length && !profiles.includes(key)) return false;
    if (excluded.length && excluded.includes(key)) return false;
    return true;
}

/** Perfiles únicos ordenados por red y nombre: [{key, red, perfil}]. */
export function profileCatalog(...rowSets) {
    const map = new Map();
    rowSets.flat().forEach((row) => {
        if (!row || isBlank(row.Red) || isBlank(row.Perfil)) return;
        const key = profileKey(row);
        if (!map.has(key)) map.set(key, { key, red: row.Red, perfil: row.Perfil });
    });
    const rank = (red) => {
        const i = NETWORK_ORDER.indexOf(red);
        return i === -1 ? NETWORK_ORDER.length : i;
    };
    return Array.from(map.values()).sort((a, b) => rank(a.red) - rank(b.red) || a.perfil.localeCompare(b.perfil, 'es'));
}

/**
 * Métricas con algún dato en las filas dadas (para ocultar columnas que la red no reporta).
 * @returns {string[]} en el orden de POST_METRICS.
 */
export function metricsWithData(rows, metrics = POST_METRICS) {
    return metrics.filter(metric => rows.some(row => num(row[metric]) !== 0));
}

/** Suma de métricas por fila. */
export function sumRows(rows, metrics) {
    const out = {};
    metrics.forEach((metric) => { out[metric] = 0; });
    rows.forEach((row) => metrics.forEach((metric) => { out[metric] += num(row[metric]); }));
    return out;
}

/**
 * Tasa de interacción: interacciones entre alcance; si la red no reporta alcance
 * (LinkedIn), contra impresiones. Se calcula fila por fila y luego se agrega.
 */
export function engagementRate(rows) {
    let interactions = 0;
    let base = 0;
    rows.forEach((row) => {
        const reach = num(row.Alcance);
        const denom = reach > 0 ? reach : num(row.Impresiones);
        if (denom > 0) {
            interactions += num(row.Interacciones);
            base += denom;
        }
    });
    return base > 0 ? interactions / base : null;
}

/** Variación relativa; null si no hay base. */
export function change(current, previous) {
    if (current == null || previous == null || !previous) return null;
    return (current - previous) / Math.abs(previous);
}

/**
 * Foto de seguidores de cada perfil a una fecha: la más reciente con fecha ≤ `date` y, si se
 * pide, ≥ `notBefore`. Map<perfilKey, {value, date}>.
 */
export function snapshotsAt(rows, date, notBefore = '') {
    const out = new Map();
    rows.forEach((row) => {
        const day = String(row.Fecha || '').slice(0, 10);
        if (!day || day > date || (notBefore && day < notBefore)) return;
        const key = profileKey(row);
        const prev = out.get(key);
        if (!prev || day > prev.date) out.set(key, { value: num(row.Seguidores), date: day });
    });
    return out;
}

/**
 * Seguidores al inicio y al cierre del periodo por perfil, desde las fotos diarias.
 * Inicio = última foto antes del primer día; si no hay, la primera foto dentro del periodo.
 */
export function followerSnapshotDelta(rows, { from, to }) {
    const start = `${from}-01`;
    const end = monthEnd(to);
    const atEnd = snapshotsAt(rows, end, start);
    const before = snapshotsAt(rows, addDays(start, -1));
    const firstInside = new Map();
    rows.forEach((row) => {
        const day = String(row.Fecha || '').slice(0, 10);
        if (!day || day < start || day > end) return;
        const key = profileKey(row);
        const prev = firstInside.get(key);
        if (!prev || day < prev.date) firstInside.set(key, { value: num(row.Seguidores), date: day });
    });
    const out = new Map();
    atEnd.forEach((endSnap, key) => {
        const startSnap = before.get(key) || firstInside.get(key) || null;
        const delta = startSnap ? endSnap.value - startSnap.value : null;
        out.set(key, {
            start: startSnap,
            end: endSnap,
            delta,
            deltaPct: startSnap && startSnap.value ? delta / startSnap.value : null,
        });
    });
    return out;
}

function addDays(day, delta) {
    const [y, m, d] = day.split('-').map(Number);
    const date = new Date(y, m - 1, d + delta);
    return `${monthKey(date)}-${String(date.getDate()).padStart(2, '0')}`;
}

/** Filas mensuales dentro de un rango de meses. */
export function inPeriod(rows, { from, to }, field = 'Mes') {
    return rows.filter((row) => {
        const month = String(row[field] || '').slice(0, 7);
        return month >= from && month <= to;
    });
}

/** ID de archivo de un enlace de Drive (…/file/d/ID/…) o null. */
export function driveFileId(url) {
    const match = String(url || '').match(/\/d\/([A-Za-z0-9_-]{10,})/) || String(url || '').match(/[?&]id=([A-Za-z0-9_-]{10,})/);
    return match ? match[1] : null;
}

/** Miniatura de Drive (la abre quien tenga acceso al archivo con su sesión de Google). */
export function driveThumbnail(url, size = 320) {
    const id = driveFileId(url);
    return id ? `https://drive.google.com/thumbnail?id=${id}&sz=w${size}` : null;
}

const compact = new Intl.NumberFormat('es-MX', { notation: 'compact', maximumFractionDigits: 1 });
const full = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 0 });

/** 1.2 M, 35.4 k… para KPIs; el número completo va en el tooltip. */
export const formatCompact = (value) => (value == null ? '—' : compact.format(value));
export const formatNumber = (value) => (value == null ? '—' : full.format(value));
export const formatPct = (value, digits = 1) => (value == null ? '—' : `${(value * 100).toFixed(digits)}%`);
export const formatSigned = (value) => (value == null ? '—' : `${value > 0 ? '+' : ''}${full.format(value)}`);

/** Segundos → «1 h 02 min», «3 min 20 s» o «45 s». */
export function formatDuration(seconds) {
    const s = Math.round(num(seconds));
    if (!s) return '—';
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (h) return `${full.format(h)} h ${String(m).padStart(2, '0')} min`;
    if (m) return `${m} min ${String(s % 60).padStart(2, '0')} s`;
    return `${s} s`;
}

export const isDurationMetric = (metric) => /\(s\)$/.test(metric);

/** Valor formateado de una métrica de publicación. */
export const formatMetric = (metric, value) => (isDurationMetric(metric) ? formatDuration(value) : formatNumber(num(value)));

/** Cubeta de un día «YYYY-MM-DD» según el grano: el día, el lunes de su semana o su mes. */
export function bucketOf(day, grain) {
    const text = String(day || '').slice(0, 10);
    if (grain === 'month') return text.slice(0, 7);
    if (grain === 'week') {
        const [y, m, d] = text.split('-').map(Number);
        const date = new Date(y, m - 1, d);
        const offset = (date.getDay() + 6) % 7;
        date.setDate(date.getDate() - offset);
        return `${monthKey(date)}-${String(date.getDate()).padStart(2, '0')}`;
    }
    return text;
}

/**
 * Serie de seguidores desde filas diarias o mensuales.
 * - Total: último valor conocido de cada perfil en la cubeta (se arrastra si un perfil no
 *   reporta ese día) y se suma por serie.
 * - Nuevos / Perdidos / Neto: suma de la cubeta.
 * @param {Array} rows filas con Red, Perfil, `dateField`, `totalField`, Nuevos, Perdidos, Neto
 * @param {{grain: string, seriesOf: function, dateField: string, totalField: string}} options
 * @returns {{buckets: string[], series: string[], totals: Object[], movement: Object[]}}
 */
export function followerSeries(rows, { grain, seriesOf, dateField = 'Fecha', totalField = 'Seguidores Totales' }) {
    const sorted = [...rows].sort((a, b) => String(a[dateField]).localeCompare(String(b[dateField])));
    const buckets = [];
    const seen = new Set();
    sorted.forEach((row) => {
        const bucket = grain === 'month' ? String(row[dateField]).slice(0, 7) : bucketOf(row[dateField], grain);
        if (!seen.has(bucket)) { seen.add(bucket); buckets.push(bucket); }
    });
    const seriesNames = new Set();
    const last = new Map();
    const movement = new Map(buckets.map(b => [b, { bucket: b, Nuevos: 0, Perdidos: 0, Neto: 0 }]));
    const totals = [];
    let index = 0;
    buckets.forEach((bucket) => {
        while (index < sorted.length) {
            const row = sorted[index];
            const rowBucket = grain === 'month' ? String(row[dateField]).slice(0, 7) : bucketOf(row[dateField], grain);
            if (rowBucket !== bucket) break;
            const name = seriesOf(row);
            seriesNames.add(name);
            const total = Number(row[totalField]);
            if (Number.isFinite(total) && total > 0) last.set(profileKey(row), { name, value: total });
            const mov = movement.get(bucket);
            mov.Nuevos += num(row.Nuevos);
            mov.Perdidos += num(row.Perdidos);
            mov.Neto += num(row.Neto);
            index += 1;
        }
        const point = { bucket };
        last.forEach(({ name, value }) => { point[name] = (point[name] || 0) + value; });
        totals.push(point);
    });
    return { buckets, series: Array.from(seriesNames), totals, movement: Array.from(movement.values()) };
}

/**
 * Resumen por perfil en el periodo: seguidores al inicio (último valor antes del periodo o
 * primero dentro) y al cierre, movimiento y si hay días estimados.
 */
export function followerProfileSummary(rows, { from, to }, { dateField = 'Fecha', totalField = 'Seguidores Totales' } = {}) {
    const start = `${from}-01`;
    const end = monthEnd(to);
    const map = new Map();
    [...rows]
        .sort((a, b) => String(a[dateField]).localeCompare(String(b[dateField])))
        .forEach((row) => {
            const day = String(row[dateField]).slice(0, 10);
            const key = profileKey(row);
            if (!map.has(key)) {
                map.set(key, { key, Red: row.Red, Perfil: row.Perfil, start: null, end: null, Nuevos: 0, Perdidos: 0, Neto: 0, days: 0, estimated: 0 });
            }
            const item = map.get(key);
            const total = Number(row[totalField]);
            const valid = Number.isFinite(total) && total > 0;
            if (day < start) {
                if (valid) item.start = { value: total, date: day };
                return;
            }
            if (day > end) return;
            if (valid && !item.start) item.start = { value: total, date: day };
            if (valid) item.end = { value: total, date: day };
            item.Nuevos += num(row.Nuevos);
            item.Perdidos += num(row.Perdidos);
            item.Neto += num(row.Neto);
            item.days += 1;
            if (row['Fuente Total'] === 'reconstruido') item.estimated += 1;
        });
    return Array.from(map.values())
        .filter(item => item.days > 0)
        .map(item => ({
            ...item,
            change: item.start && item.end ? item.end.value - item.start.value : null,
            changePct: item.start && item.end && item.start.value ? (item.end.value - item.start.value) / item.start.value : null,
        }));
}

/** «16 oct», «sem. 13 oct» u «oct 2026» para el eje de una cubeta. */
export function bucketLabel(bucket, grain) {
    if (grain === 'month') return periodLabel({ from: bucket, to: bucket });
    const [y, m, d] = bucket.split('-').map(Number);
    const text = new Date(y, m - 1, d).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' }).replace('.', '');
    return grain === 'week' ? `sem. ${text}` : text;
}

/** Un día antes de «YYYY-MM-DD». */
export const dayBefore = (day) => addDays(day, -1);

/**
 * Une por mes y perfil las publicaciones mensuales con el movimiento de seguidores
 * (Nuevos, Perdidos, Neto), para que una fórmula personal pueda usar ambos.
 */
export function mergeMonthly(postsMonthly, followersMonthly) {
    const map = new Map();
    const keyOf = (row) => `${row.Mes}|${profileKey(row)}`;
    postsMonthly.forEach((row) => { map.set(keyOf(row), { ...row }); });
    followersMonthly.forEach((row) => {
        const key = keyOf(row);
        const target = map.get(key) || { Mes: row.Mes, Red: row.Red, Perfil: row.Perfil };
        target.Nuevos = num(row.Nuevos);
        target.Perdidos = num(row.Perdidos);
        target.Neto = num(row.Neto);
        map.set(key, target);
    });
    return Array.from(map.values());
}
